import type { ChainStepRow, InstrumentRow, InstrumentStatus, RequirementRow } from '@api/replenishment-engine';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Badge, Button, Group, Switch, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, useState, type FC, type ReactElement, type ReactNode } from 'react';

import { dayLabel, localDateKey, whenLabel } from './dates';
import { groupDips, mergeChains, RELAY_NOTE, unlockLabel, waitsOnLabel, type Dip } from './dips';
import classes from './engine.module.css';
import { LAND_LABEL, STATUS_COLOR } from './status';

const n = (v: number, maxDecimals = 0): string => formatCount(v, { maxDecimals });

/** A cell opened from the SKU table: the dip's head row and the parts the cell summarises (one for a part row, all for a dip row). */
export type CellTarget = { kind: 'SKU' | 'MATERIALS' | 'TRANSFERS' | 'ORDERS'; row: RequirementRow; parts: RequirementRow[] };

export const StatusBadge: FC<{ status: InstrumentStatus }> = ({ status }) => (
    <Badge size="xs" variant="light" color={STATUS_COLOR[status]} tt="none">
        {status.toLowerCase()}
    </Badge>
);

export const Lines: FC<{ lines: Array<{ text: ReactNode; sub?: ReactNode; late?: boolean; strong?: boolean; tone?: 'red' | 'green' | 'orange' }> }> = ({ lines }) =>
    lines.length === 0 ? (
        <Text size="sm" c="dimmed">
            —
        </Text>
    ) : (
        <div className={classes.lines}>
            {lines.map((l, i) => (
                <div key={i}>
                    {l.text !== '' && (
                        <div className={classes.line} data-late={l.late ? 'true' : undefined} data-strong={l.strong ? 'true' : undefined}>
                            {l.text}
                        </div>
                    )}
                    {l.sub != null && l.sub !== '' && (
                        <div className={classes.sub} data-tone={l.tone}>
                            {l.sub}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );

/** One decision for a set of instruments: approve what is still proposed, show the rest. */
const ApproveCell: FC<{ instruments: Array<Pick<InstrumentRow, 'id' | 'status'>>; label?: string; onApprove: (ids: string[]) => void; onSave?: (ids: string[]) => void }> = ({ instruments, label, onApprove, onSave }) => {
    const open = instruments.filter((i) => i.status === 'PROPOSED').map((i) => i.id);
    if (instruments.length === 0) {
        return (
            <Text size="xs" c="dimmed">
                nothing to do
            </Text>
        );
    }
    if (open.length === 0) return <StatusBadge status={instruments[0].status} />;
    return (
        <Group gap={6} wrap="nowrap">
            <Button
                size="compact-xs"
                onClick={(e) => {
                    e.stopPropagation();
                    onApprove(open);
                }}
            >
                {label ?? 'Approve'}
            </Button>
            {onSave && (
                <Button
                    size="compact-xs"
                    variant="subtle"
                    color="grape"
                    title="Keep these numbers as they stand, without creating any document"
                    onClick={(e) => {
                        e.stopPropagation();
                        onSave(open);
                    }}
                >
                    Save
                </Button>
            )}
            {open.length < instruments.length && (
                <Text size="10px" c="dimmed">
                    {instruments.length - open.length} done
                </Text>
            )}
        </Group>
    );
};

const Clickable: FC<{ onClick: () => void; children: ReactNode }> = ({ onClick, children }) => (
    <div
        className={classes.cellLink}
        role="button"
        tabIndex={0}
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        onKeyDown={(e) => {
            if (e.key === 'Enter') onClick();
        }}
    >
        {children}
    </div>
);

const routeText = (r: RequirementRow): string => {
    if (r.route === 'MAKE_AS_SFG') return `as ${r.madeAsProductId} at ${r.facilityId ?? '—'}`;
    if (r.route === 'MAKE') return `at ${r.facilityId ?? '—'}`;
    if (r.route === 'BUY') return `from ${r.facilityId ?? '—'}`;
    return 'no route';
};

const shortName = (s: string, words = 2): string => s.split(' ').slice(0, words).join(' ');

const byPlaceBy = <T extends { placeBy: string | null }>(steps: T[]): T[] => [...steps].sort((a, b) => (a.placeBy ?? '').localeCompare(b.placeBy ?? ''));

// ------------------------------------------------------------------ SKU table (dip anchored)

/**
 * A row of the SKU table: a dip (one per productId + seq, its parts folded under it) or,
 * when expanded, one of its parts. `row` is the dip's head (part 1, or the part-0 row) for
 * a dip and the part itself for a part; `parts` is what the row's cells summarise.
 */
export type SkuRow = { kind: 'DIP' | 'PART'; key: string; dip: Dip; row: RequirementRow; parts: RequirementRow[]; instruments: InstrumentRow[] };

const skuRowsOf = (rows: RequirementRow[]): SkuRow[] =>
    groupDips(rows).map((dip) => ({ kind: 'DIP', key: `dip:${dip.key}`, dip, row: dip.head, parts: dip.parts, instruments: dip.instruments }));

/** A dip expands only when it has more than one part: a single part is the dip row itself. */
const partRowsOf = (r: SkuRow): SkuRow[] | undefined => (r.kind === 'DIP' && r.parts.length > 1 ? r.parts.map((part) => ({ kind: 'PART', key: `part:${part.id}`, dip: r.dip, row: part, parts: [part], instruments: part.instruments })) : undefined);

/** A part's own run or order is late when its start has come and it is still only proposed. */
const partIsLate = (part: RequirementRow, today: string): boolean => Boolean(part.startDate && part.startDate <= today && part.instruments.some((i) => i.id === part.instrumentId && i.status === 'PROPOSED'));

/** The materials across the row's parts, one line per material, short when any part is short of it. */
/**
 * Each material of a dip across its parts, with where its units come from: on the factory
 * floor, by transfer, by order, or short (nothing carries it). A part's chain lists a
 * material once as a summary (no instrument) or once per order that carries it.
 */
const materialsOf = (parts: RequirementRow[]): Array<{ itemId: string; itemName: string | null; need: number; floor: number; moved: number; ordered: number; short: number }> => {
    const out = new Map<string, { itemId: string; itemName: string | null; need: number; floor: number; moved: number; ordered: number; short: number }>();
    for (const p of parts) {
        const seen = new Set<string>();
        for (const c of p.chain) {
            if (c.step !== 'MATERIAL') continue;
            const cur = out.get(c.itemId) ?? { itemId: c.itemId, itemName: c.itemName, need: 0, floor: 0, moved: 0, ordered: 0, short: 0 };
            if (!seen.has(c.itemId)) {
                seen.add(c.itemId);
                cur.need += c.qty;
                cur.floor += c.available;
                cur.moved += c.movable;
            }
            if (c.instrumentId) cur.ordered += c.short;
            else cur.short += c.short;
            cur.itemName = c.itemName ?? cur.itemName;
            out.set(c.itemId, cur);
        }
    }
    return [...out.values()];
};

export const SkuTable: FC<{ rows: RequirementRow[]; loading: boolean; title: string; subtitle: string; onOpen: (t: CellTarget) => void; onApprove: (ids: string[]) => void; onSave?: (ids: string[]) => void }> = ({ rows, loading, title, subtitle, onOpen, onApprove, onSave }) => {
    const today = localDateKey();
    const dips = useMemo(() => skuRowsOf(rows), [rows]);
    // A SKU with no route and nothing to do is a packaging master or an unmapped item, and one
    // that never dips has nothing to act on: noise in a list of things to do. Hidden by
    // default, one switch away.
    const [showUnsourceable, setShowUnsourceable] = useState(false);
    const isNoise = (r: SkuRow): boolean => (r.row.landStatus === 'UNSOURCEABLE' || r.row.landStatus === 'NO_DIP') && r.instruments.length === 0;
    const unsourceable = useMemo(() => dips.filter(isNoise).length, [dips]);
    const visible = useMemo(() => (showUnsourceable ? dips : dips.filter((r) => !isNoise(r))), [dips, showUnsourceable]);
    const columns = useMemo<DataSheetColumn<SkuRow>[]>(
        () => [
            {
                key: 'sku',
                header: 'SKU',
                width: 250,
                sticky: true,
                sortable: true,
                valueAccessor: (r) => r.row.sku,
                render: (_v, r): ReactElement => {
                    if (r.kind === 'PART') {
                        const p = r.row;
                        return <Lines lines={[{ text: `Part ${p.part} of ${p.parts}${p.instruments.some((i) => i.id === p.instrumentId && i.pinned?.qty != null) ? ' · fixed by hand' : ''}`, strong: true, sub: `making ${n(p.qty)} of ${p.neededAtLanding != null ? n(p.neededAtLanding) : '—'} needed by ${dayLabel(p.landDate)}` }]} />;
                    }
                    return <Lines lines={[{ text: r.row.sku, strong: true }, { text: r.row.productName ?? '', sub: `${routeText(r.row)} · on hand ${n(r.row.onHand)}${r.row.coverDays != null ? ` · ${n(r.row.coverDays)}d` : ''}` }]} />;
                },
            },
            {
                key: 'dip',
                header: 'Dips',
                width: 180,
                sortable: true,
                valueAccessor: (r) => (r.kind === 'PART' ? (r.row.startDate ?? '') : (r.row.dipDate ?? '')),
                render: (_v, r): ReactElement => {
                    if (r.kind === 'PART') return <Lines lines={[{ text: `starts ${whenLabel(r.row.startDate, today)}`, late: partIsLate(r.row, today), sub: `lands ${dayLabel(r.row.landDate)}` }]} />;
                    const l = LAND_LABEL[r.row.landStatus];
                    return (
                        <Clickable onClick={() => onOpen({ kind: 'SKU', row: r.row, parts: r.parts })}>
                            <Lines lines={[{ text: `${whenLabel(r.row.dipDate, today)} · zero ${dayLabel(r.row.zeroDate)}`, late: Boolean(r.row.dipDate && r.row.dipDate <= today), sub: r.row.stockoutDays > 0 ? `${r.row.stockoutDays} days at zero` : l.text, tone: l.tone }]} />
                        </Clickable>
                    );
                },
            },
            {
                key: 'qty',
                header: 'Qty',
                width: 110,
                align: 'right',
                sortable: true,
                valueAccessor: (r) => (r.kind === 'PART' ? r.row.qty : r.row.requiredQty),
                render: (_v, r): ReactElement => {
                    if (r.kind === 'PART') return <Lines lines={[{ text: n(r.row.qty), strong: true, sub: r.row.neededAtLanding != null ? `of ${n(r.row.neededAtLanding)} needed` : '' }]} />;
                    const count = r.parts.length;
                    const made = r.parts.reduce((s, p) => s + p.qty, 0);
                    // One dip, one run: what the day let it make, of what the curve asked for at its landing.
                    return <Lines lines={[{ text: n(count > 0 ? made : r.row.requiredQty), strong: true, sub: count > 1 ? `of ${n(r.row.requiredQty)} needed · in ${count} parts` : count === 1 && made < r.row.requiredQty ? `of ${n(r.row.requiredQty)} needed` : count === 0 ? (r.row.landStatus === 'COVERED' ? 'covered' : r.row.landStatus === 'UNSOURCEABLE' ? 'no route' : '') : '', tone: count > 0 && made < r.row.requiredQty ? 'orange' : undefined }]} />;
                },
            },
            {
                key: 'lands',
                header: 'Lands',
                width: 150,
                sortable: true,
                valueAccessor: (r) => (r.kind === 'PART' ? (r.row.landDate ?? '') : (r.dip.firstLand ?? '')),
                render: (_v, r): ReactElement => {
                    if (r.kind === 'PART') {
                        const waits = waitsOnLabel(r.row.gatingMaterialId, r.row.chain);
                        return <Lines lines={[{ text: dayLabel(r.row.landDate), sub: waits ? `next part waits on ${waits}` : r.row.part === r.row.parts ? 'last part' : '' }]} />;
                    }
                    if (r.parts.length === 0) return <Lines lines={[{ text: r.row.landStatus === 'COVERED' ? dayLabel(r.row.landDate) : '—', sub: r.row.landStatus === 'COVERED' ? 'already on the book' : r.row.landStatus === 'UNSOURCEABLE' ? 'no route' : '' }]} />;
                    const { firstLand, lastLand } = r.dip;
                    return <Lines lines={[{ text: dayLabel(firstLand), late: Boolean(r.row.zeroDate && firstLand && firstLand > r.row.zeroDate), sub: r.parts.length > 1 && lastLand && lastLand !== firstLand ? `then ${dayLabel(lastLand)}` : '' }]} />;
                },
            },
            {
                key: 'first',
                header: 'First part',
                width: 210,
                sortable: true,
                valueAccessor: (r) => (r.kind === 'PART' ? (r.row.unlockedBy ?? '') : (r.parts[0]?.startDate ?? '')),
                render: (_v, r): ReactElement => {
                    if (r.kind === 'PART') return <Lines lines={[{ text: `unlocked by ${unlockLabel(r.row.unlockedBy, r.row.chain)}`, sub: r.row.instrumentId ? (r.row.route === 'BUY' ? 'its FG order' : 'its work order') : '' }]} />;
                    if (r.parts.length === 0) {
                        const covered = r.row.landStatus === 'COVERED';
                        return <Lines lines={[{ text: covered ? 'covered' : r.row.landStatus === 'UNSOURCEABLE' ? 'unsourceable' : '—', strong: true, sub: covered ? 'a landing already on the book answers this dip' : r.row.landStatus === 'UNSOURCEABLE' ? 'no factory, vendor or SFG route' : '', tone: covered ? 'green' : 'red' }]} />;
                    }
                    const first = r.parts[0];
                    const waits = waitsOnLabel(first.gatingMaterialId, first.chain);
                    return <Lines lines={[{ text: `${n(first.qty)} · ${first.route === 'BUY' ? 'order' : 'start'} ${whenLabel(first.startDate, today)}`, strong: true, late: partIsLate(first, today), sub: `unlocked by ${unlockLabel(first.unlockedBy, first.chain)}${waits ? ` · then waits on ${waits}` : ''}` }]} />;
                },
            },
            {
                key: 'material',
                header: 'Material at factory',
                width: 190,
                render: (_v, r): ReactElement => {
                    const mats = materialsOf(r.parts);
                    if (r.row.route === 'BUY' || mats.length === 0) {
                        return (
                            <Text size="sm" c="dimmed">
                                —
                            </Text>
                        );
                    }
                    const short = mats.filter((c) => c.short > 0);
                    const onFloor = mats.filter((c) => c.floor >= c.need - 1e-9).length;
                    const byTransfer = mats.filter((c) => c.moved > 0).length;
                    const byOrder = mats.filter((c) => c.ordered > 0).length;
                    const names = short.slice(0, 3).map((c) => shortName(c.itemName ?? c.itemId)).join(', ');
                    const sources = [byTransfer ? `${byTransfer} by transfer` : '', byOrder ? `${byOrder} by order` : ''].filter(Boolean).join(' · ');
                    return (
                        <Clickable onClick={() => onOpen({ kind: 'MATERIALS', row: r.row, parts: r.parts })}>
                            <Lines
                                lines={[
                                    {
                                        text: (
                                            <Badge size="xs" color={short.length > 0 ? (short.length === mats.length ? 'red' : 'orange') : onFloor === mats.length ? 'teal' : onFloor > 0 ? 'yellow' : 'gray'} variant="light" tt="none">
                                                {short.length > 0 ? `short ${short.length} of ${mats.length}` : onFloor === mats.length ? `all ${mats.length} on the floor` : onFloor > 0 ? `${onFloor} of ${mats.length} on the floor` : `none of ${mats.length} on the floor`}
                                            </Badge>
                                        ),
                                        sub: short.length > 0 ? `${names}${short.length > 3 ? ` +${short.length - 3}` : ''}` : sources || 'nothing to bring in',
                                    },
                                ]}
                            />
                        </Clickable>
                    );
                },
            },
            {
                key: 'transfer',
                header: 'Transfer',
                width: 170,
                render: (_v, r): ReactElement => {
                    const moves = mergeChains(r.parts).filter((c) => c.step === 'TRANSFER');
                    if (moves.length === 0) return <Lines lines={[{ text: '—', sub: r.row.route === 'BUY' || r.parts.length === 0 ? '' : 'nothing movable' }]} />;
                    const first = byPlaceBy(moves)[0];
                    // One quantity only when every move shares a unit; kilos and pieces do not add.
                    const oneUnit = moves.every((m) => m.unit === first.unit);
                    const total = moves.reduce((s, m) => s + m.qty, 0);
                    const relays = moves.filter((m) => m.note === RELAY_NOTE).length;
                    const what = relays === 0 ? 'hub stock' : relays === moves.length ? `relaying ${relays} PO${relays === 1 ? '' : 's'}` : `${relays} relaying POs · ${moves.length - relays} hub stock`;
                    return (
                        <Clickable onClick={() => onOpen({ kind: 'TRANSFERS', row: r.row, parts: r.parts })}>
                            <Lines lines={[{ text: `${whenLabel(first.placeBy, today)} · ${oneUnit ? `${n(total, 1)}${first.unit ? ` ${first.unit}` : ''}` : `${moves.length} materials`}`, late: Boolean(first.placeBy && first.placeBy <= today && first.status === 'PROPOSED'), sub: `${oneUnit ? `${moves.length} material${moves.length === 1 ? '' : 's'} · ` : ''}${what} · ${first.fromSiteId} → ${first.toSiteId}` }]} />
                        </Clickable>
                    );
                },
            },
            {
                key: 'orders',
                header: 'RM / PM orders',
                width: 190,
                render: (_v, r): ReactElement => {
                    const seen = new Set<string>();
                    const pos: ChainStepRow[] = mergeChains(r.parts).filter((c) => c.step === 'MATERIAL' && c.instrumentId && (seen.has(c.instrumentId) ? false : (seen.add(c.instrumentId), true)));
                    if (pos.length === 0) return <Lines lines={[{ text: '—', sub: r.row.route === 'BUY' || r.parts.length === 0 ? '' : 'all on floor' }]} />;
                    const first = byPlaceBy(pos)[0];
                    const open = pos.filter((c) => c.status === 'PROPOSED').length;
                    // Where the orders land: the factory direct, or a hub a transfer relays from.
                    const byDest = new Map<string, number>();
                    for (const c of pos) byDest.set(c.toSiteId ?? '—', (byDest.get(c.toSiteId ?? '—') ?? 0) + 1);
                    const dests = [...byDest.entries()].sort((a, b) => b[1] - a[1]).map(([site, k]) => `${k} → ${site}`).join(' · ');
                    return (
                        <Clickable onClick={() => onOpen({ kind: 'ORDERS', row: r.row, parts: r.parts })}>
                            <Lines lines={[{ text: `${whenLabel(first.placeBy, today)} · ${pos.length} PO${pos.length === 1 ? '' : 's'}`, late: Boolean(first.placeBy && first.placeBy <= today && first.status === 'PROPOSED'), sub: `${dests}${open < pos.length ? ` · ${pos.length - open} approved` : ''}` }]} />
                        </Clickable>
                    );
                },
            },
            // A dip approves every part's instrument; the review sheet brings each one's chain along.
            { key: 'approve', header: 'Approve', width: 170, render: (_v, r): ReactElement => <ApproveCell instruments={r.instruments} label={r.kind === 'DIP' && r.parts.length > 1 ? `Approve ${r.parts.length} parts` : undefined} onApprove={onApprove} onSave={onSave} /> },
        ],
        [today, onOpen, onApprove, onSave],
    );
    return (
        <DataSheet<SkuRow>
            data={visible}
            columns={columns}
            isLoading={loading}
            getRowKey={(r) => r.key}
            getSubRows={partRowsOf}
            getRowClassName={(r) => (r.kind === 'PART' ? classes.partRow : '')}
            rowHeight={56}
            enableSearch
            searchPlaceholder="Search SKU or name…"
            title={title}
            subtitle={subtitle}
            onRowClick={(r) => onOpen({ kind: 'SKU', row: r.row, parts: r.parts })}
            toolbarControls={unsourceable > 0 ? <Switch size="xs" label={`show ${unsourceable} with no route`} checked={showUnsourceable} onChange={(e) => setShowUnsourceable(e.currentTarget.checked)} /> : undefined}
        />
    );
};
